import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Trash2, Search, ChevronDown, User } from 'lucide-react';
import { getDeviceId } from '../utils/deviceTracking';
import AdminShortcut from '../components/AdminShortcut';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reviews, setReviews] = useState([]);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState('date'); // default: most recent first (chronological)
  const [loading, setLoading] = useState(true);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showMyReviews, setShowMyReviews] = useState(false);
  const [deviceId] = useState(() => getDeviceId());
  
  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const sortOptions = [
    { value: 'rating_date', label: 'Highest Rating, Recent' },
    { value: 'rating', label: 'Highest Rating' },
    { value: 'date', label: 'Most Recent' },
    { value: 'city_rating', label: 'City, then Rating' },
    { value: 'reviewer', label: 'Reviewer Name' }
  ];

  useEffect(() => {
    const searchQuery = searchParams.get('search') || '';
    setSearch(searchQuery);
    fetchReviews();
  }, [searchParams]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/public/reviews`);
      setReviews(response.data);
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  // Helper to parse address into components
  const parseAddress = (address) => {
    if (!address) return { street: '', city: '', stateZip: '' };
    
    const parts = address.split(',').map(p => p.trim());
    
    if (parts.length === 1) {
      // Just one part - could be city name or street
      return { street: '', city: parts[0], stateZip: '' };
    }
    
    if (parts.length === 2) {
      // Format: "City, ST" or "Street, City"
      // Check if second part has state abbreviation pattern
      if (/^[A-Z]{2}(\s|$)/.test(parts[1])) {
        return { street: '', city: parts[0], stateZip: parts[1] };
      }
      return { street: parts[0], city: parts[1], stateZip: '' };
    }
    
    if (parts.length === 3) {
      // Format: "Street, City, ST ZIP" or "Street, City, Country"
      return { street: parts[0], city: parts[1], stateZip: parts[2] };
    }
    
    if (parts.length >= 4) {
      // Format: "Street, City, ST ZIP, USA" - ignore last part if it's country
      const lastPart = parts[parts.length - 1].toUpperCase();
      if (lastPart === 'USA' || lastPart === 'US' || lastPart.length > 10) {
        return { 
          street: parts[0], 
          city: parts[1], 
          stateZip: parts[2] 
        };
      }
      return { 
        street: parts.slice(0, -2).join(', '), 
        city: parts[parts.length - 2], 
        stateZip: parts[parts.length - 1] 
      };
    }
    
    return { street: address, city: '', stateZip: '' };
  };

  // Get city for search/sort
  const getCityFromReview = (review) => {
    if (review.city) return review.city;
    const parsed = parseAddress(review.address);
    return parsed.city || '';
  };

  // Filter and sort reviews
  const filteredAndSortedReviews = useMemo(() => {
    let result = [...reviews];
    
    // Filter out test reviews (case insensitive)
    result = result.filter(review => {
      const name = (review.restaurant_name || '').toLowerCase();
      return !name.includes('test');
    });
    
    // Filter by "My Reviews" - show only reviews from this device
    if (showMyReviews && deviceId) {
      result = result.filter(review => review.device_id === deviceId);
    }
    
    // Filter by search - always search all fields
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      const searchDigitsGlobal = search.replace(/\D/g, '');
      result = result.filter(review => {
        // Search across all fields
        const restaurantMatch = review.restaurant_name?.toLowerCase().includes(searchLower);
        const cityMatch = getCityFromReview(review).toLowerCase().includes(searchLower);
        const addressMatch = (review.address || '').toLowerCase().includes(searchLower);
        const phoneDigitsAll = (review.phone || '').replace(/\D/g, '');
        const phoneMatch = searchDigitsGlobal && phoneDigitsAll.includes(searchDigitsGlobal);
        const reviewerMatch = review.reviewer_name?.toLowerCase().includes(searchLower);
        return restaurantMatch || cityMatch || addressMatch || phoneMatch || reviewerMatch;
      });
    }
    
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'rating_date':
          // First by rating (highest first), then by date (most recent first)
          const ratingDiff = (b.overall_rating || 0) - (a.overall_rating || 0);
          if (ratingDiff !== 0) return ratingDiff;
          return new Date(b.created_at) - new Date(a.created_at);
        case 'city_rating':
          // First by city, then by rating (highest first)
          const cityA = getCityFromReview(a);
          const cityB = getCityFromReview(b);
          const cityCompare = cityA.localeCompare(cityB);
          if (cityCompare !== 0) return cityCompare;
          return (b.overall_rating || 0) - (a.overall_rating || 0);
        case 'rating':
          return (b.overall_rating || 0) - (a.overall_rating || 0);
        case 'reviewer':
          return (a.reviewer_name || '').localeCompare(b.reviewer_name || '');
        case 'date':
          return new Date(b.created_at) - new Date(a.created_at);
        default:
          return 0;
      }
    });
    
    return result;
  }, [reviews, search, sortBy, showMyReviews, deviceId]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('Please enter your password');
      return;
    }
    
    setDeletingAccount(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/delete-account`, 
        { password: deletePassword },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      toast.success('Account deleted successfully');
      localStorage.removeItem('token');
      navigate('/');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const getRatingDisplay = (rating) => {
    switch(rating) {
      case 1:
        return { 
          image: 'https://customer-assets.emergentagent.com/job_ab3cc9c0-1e7a-4d44-b103-5b0d8320f833/artifacts/o44vc304_IMG_3041.jpeg',
          label: 'MAJOR ISSUES', 
          color: 'text-red-600 bg-red-100 border-red-300' 
        };
      case 2:
        return { 
          image: 'https://static.thenounproject.com/png/5000928-200.png',
          label: 'NEEDS ATTENTION', 
          color: 'text-orange-600 bg-orange-100 border-orange-300' 
        };
      case 3:
        return { 
          emoji: '👌',
          label: "IT'S OK", 
          color: 'text-yellow-600 bg-yellow-100 border-yellow-300' 
        };
      case 4:
        return { 
          image: 'https://customer-assets.emergentagent.com/job_ab3cc9c0-1e7a-4d44-b103-5b0d8320f833/artifacts/wear2a7c_IMG_3052.jpeg',
          label: 'EXCEPTIONAL', 
          color: 'text-green-600 bg-green-100 border-green-300' 
        };
      default:
        return { 
          emoji: '❓',
          label: 'NOT PROVIDED', 
          color: 'text-gray-500 bg-gray-100 border-gray-300' 
        };
    }
  };

  return (
    <div className="min-h-screen bg-yellow-400">
      {/* Hero Header - Matching Home Screen */}
      <div className="bg-yellow-400 px-4 pt-6 pb-4">
        <div className="max-w-md mx-auto flex flex-col items-center">
          {/* Original Logo Image with TM - Same as Home Screen */}
          <div className="relative mb-4 w-full">
            <img 
              src="https://customer-assets.emergentagent.com/job_loo-tracker/artifacts/2igfkpva_Background%20for%20APP%20Home%20Screen%20no%20head.jpg" 
              alt="The Loo Report - Know Before You Go!"
              className="w-full h-auto object-contain"
            />
            <span className="absolute text-black text-base font-bold" style={{ fontFamily: 'Arial, sans-serif', bottom: '25%', right: '5%' }}>™</span>
          </div>

          {/* Navigation Buttons - Same style as Home Screen */}
          <div className="w-full space-y-2 mb-4">
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-white hover:bg-gray-100 text-gray-700 font-bold py-3 px-6 rounded-full border-2 border-gray-300 shadow-md"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="home-btn"
            >
              🏠 HOME
            </Button>
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-full border-2 border-blue-700 shadow-md"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="submit-btn"
            >
              ✍️ POST A REVIEW
            </Button>
            {user && user.is_admin && (
              <Button
                onClick={() => navigate('/admin')}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-full border-2 border-red-700 shadow-md"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="admin-btn"
              >
                ⚙️ ADMIN
              </Button>
            )}
            {user && (
              <Button
                onClick={() => {
                  localStorage.removeItem('token');
                  navigate('/');
                  window.location.reload();
                }}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-full border-2 border-yellow-700 shadow-md"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="logout-btn"
              >
                LOGOUT ({user.name?.split(' ')[0]?.toUpperCase()})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Page Title */}
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white border-3 border-black rounded-lg p-3 mb-4 shadow-md">
          <h1 className="text-red-600 font-bold text-center"
              style={{
                fontSize: '1.5rem',
                fontFamily: 'Fredoka, sans-serif',
                letterSpacing: '0.05em',
                textTransform: 'uppercase'
              }}>
            PAST REVIEWS
          </h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pb-8">
        {/* Search and Sort Controls - Simplified */}
        <div className="bg-white border-4 border-black rounded-lg p-4 mb-6 shadow-lg">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
            {/* Search Input - Full width, no dropdown */}
            <div className="flex-1 relative flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by restaurant name or city..."
                  className="pl-10 border-2 border-black rounded-lg font-bold h-12 w-full"
                  style={{ fontFamily: 'Fredoka, sans-serif' }}
                  data-testid="search-input"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white font-bold border-2 border-black px-6 h-12 text-sm"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="search-btn"
              >
                SEARCH
              </Button>
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center justify-between w-full md:w-52 px-4 bg-red-500 text-white border-2 border-black rounded-lg font-bold text-sm h-12"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Sort By: {sortOptions.find(o => o.value === sortBy)?.label}
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>
              {showSortDropdown && (
                <div className="absolute z-20 w-full mt-1 bg-white border-2 border-black rounded-lg shadow-lg">
                  {sortOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => { setSortBy(option.value); setShowSortDropdown(false); }}
                      className={`w-full px-4 py-2 text-left hover:bg-red-100 font-bold text-sm text-black ${sortBy === option.value ? 'bg-red-200' : ''}`}
                      style={{ fontFamily: 'Fredoka, sans-serif' }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* My Reviews Toggle */}
            <Button
              type="button"
              onClick={() => setShowMyReviews(!showMyReviews)}
              className={`font-bold border-2 border-black px-4 h-12 text-sm ${
                showMyReviews 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-white hover:bg-blue-100 text-blue-600'
              }`}
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="my-reviews-btn"
            >
              <User className="w-4 h-4 mr-1" />
              MY REVIEWS
            </Button>

            {search && (
              <Button
                type="button"
                onClick={() => setSearch('')}
                className="bg-gray-500 hover:bg-gray-600 text-white font-bold border-2 border-black px-4"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Clear
              </Button>
            )}
          </form>
          
          {/* Results count - TEMPORARILY HIDDEN
          <div className="mt-3 text-sm text-gray-600 font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Showing {filteredAndSortedReviews.length} review{filteredAndSortedReviews.length !== 1 ? 's' : ''}
            {search && ` matching "${search}"`}
          </div>
          */}
        </div>

        {/* Reviews List - Compact Style */}
        {loading ? (
          <div className="text-center py-20" data-testid="loading-message">
            <p className="text-3xl text-red-600 font-bold" 
               style={{ fontFamily: 'Fredoka, sans-serif', letterSpacing: '2px' }}>Loading reviews...</p>
          </div>
        ) : filteredAndSortedReviews.length === 0 ? (
          <div className="text-center py-20" data-testid="no-reviews-message">
            <p className="text-3xl text-red-600 font-bold mb-4" 
               style={{ fontFamily: 'Fredoka, sans-serif', letterSpacing: '2px' }}>No reviews found!</p>
            <p className="text-gray-900 text-lg font-bold">
              {search ? 'Try a different search term.' : 'Be the first to drop a review.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="reviews-container">
            {filteredAndSortedReviews.map((review) => {
              const rating = getRatingDisplay(review.overall_rating);
              
              return (
                <div 
                  key={review.id} 
                  className="bg-white border-3 border-black rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow"
                  data-testid={`review-card-${review.id}`}
                >
                  {/* Compact 4-5 line layout */}
                  <div className="flex justify-between items-start gap-4">
                    {/* Left side - Main info */}
                    <div className="flex-1 min-w-0">
                      {/* Line 1: Restaurant Name */}
                      <h3 className="text-lg font-bold text-red-600 truncate"
                          style={{ fontFamily: 'Fredoka, sans-serif' }}
                          data-testid="review-restaurant-name">
                        {review.restaurant_name}
                      </h3>
                      
                      {/* Line 2: City, State/Zip - properly parsed */}
                      {(() => {
                        const parsed = parseAddress(review.address);
                        const city = review.city || parsed.city;
                        const stateZip = review.state || parsed.stateZip;
                        return (
                          <p className="text-sm font-bold text-blue-700"
                             style={{ fontFamily: 'Fredoka, sans-serif' }}
                             data-testid="review-city">
                            {city}{stateZip ? `, ${stateZip}` : ''}
                          </p>
                        );
                      })()}
                      
                      {/* Phone Number - Clickable to call */}
                      {review.phone && (
                        <a 
                          href={`tel:${review.phone.replace(/[^0-9+]/g, '')}`}
                          className="inline-flex items-center gap-1 text-sm font-bold text-green-700 hover:text-green-900 hover:underline active:text-green-800"
                          style={{ fontFamily: 'Fredoka, sans-serif' }}
                          data-testid="review-phone"
                        >
                          <span className="text-base">📞</span>
                          {review.phone}
                        </a>
                      )}
                      
                      {/* Line 3: Street Address (truncated) */}
                      {(() => {
                        const parsed = parseAddress(review.address);
                        return parsed.street ? (
                          <p className="text-sm text-gray-600 truncate"
                             style={{ fontFamily: 'Fredoka, sans-serif' }}
                             data-testid="review-address">
                            {parsed.street}
                          </p>
                        ) : null;
                      })()}
                      
                      {/* Line 4: Quick scores */}
                      <div className="flex gap-3 mt-1 text-xs font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                        <span className="text-gray-600">
                          Clean: <span className="text-red-600">{review.general_condition}/10</span>
                        </span>
                        <span className="text-gray-600">
                          Supplies: <span className="text-red-600">{review.supplies}/10</span>
                        </span>
                        <span className="text-gray-600">
                          Condition: <span className="text-red-600">{review.maintenance || 'N/A'}/10</span>
                        </span>
                      </div>
                      
                      {/* Restroom Types */}
                      {review.restroom_types && review.restroom_types.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1" data-testid="review-restroom-types">
                          {review.restroom_types.map((type, idx) => {
                            const typeLabel = type.toLowerCase();
                            let icon = '';
                            let displayName = type;
                            
                            if (typeLabel.includes('men') && !typeLabel.includes('women')) {
                              icon = '🚹';
                              displayName = "Men's";
                            } else if (typeLabel.includes('women')) {
                              icon = '🚺';
                              displayName = "Women's";
                            } else if (typeLabel.includes('gender') || typeLabel.includes('neutral') || typeLabel.includes('unisex')) {
                              icon = '🚻';
                              displayName = 'Gender Neutral';
                            } else if (typeLabel.includes('family')) {
                              icon = '👨‍👩‍👧';
                              displayName = 'Family';
                            } else if (typeLabel.includes('handicap') || typeLabel.includes('accessible') || typeLabel.includes('ada')) {
                              icon = '♿';
                              displayName = 'Accessible';
                            }
                            
                            return (
                              <span 
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-700 border border-blue-300"
                                style={{ fontFamily: 'Fredoka, sans-serif' }}
                              >
                                {icon} {displayName}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Line 5: Reviewer and Date */}
                      <div className="flex justify-between items-center mt-1 text-xs text-gray-500"
                           style={{ fontFamily: 'Fredoka, sans-serif' }}>
                        <span data-testid="review-reviewer">
                          <strong>{review.reviewer_name && !review.reviewer_name.toLowerCase().includes('anonymous') ? review.reviewer_name : 'Verified User Review'}</strong>
                        </span>
                        <span data-testid="review-date">
                          {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                    
                    {/* Right side - Overall Rating */}
                    <div className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg border-2 ${rating.color}`}
                         style={{ minWidth: '85px' }}>
                      {rating.image ? (
                        <img 
                          src={rating.image}
                          alt={rating.label}
                          className="w-10 h-10 object-contain"
                        />
                      ) : (
                        <span className="text-3xl">{rating.emoji}</span>
                      )}
                      <span className="text-xs font-bold text-center mt-1"
                            style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '0.6rem' }}>
                        {rating.label}
                      </span>
                    </div>
                  </div>
                  
                  {/* Expandable comment preview */}
                  {review.comments && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-sm text-gray-700 line-clamp-2"
                         style={{ fontFamily: 'Fredoka, sans-serif' }}
                         data-testid="review-comments">
                        &quot;{review.comments}&quot;
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white border-4 border-red-500 rounded-lg max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-red-600 font-bold flex items-center gap-2"
                         style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <Trash2 className="w-6 h-6" />
              Delete Account
            </DialogTitle>
            <DialogDescription className="text-gray-700">
              This action is <strong>permanent</strong> and cannot be undone. All your reviews and data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="delete-password" className="text-gray-700 font-bold">
                Enter your password to confirm:
              </Label>
              <Input
                id="delete-password"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Your password"
                className="mt-2 border-2 border-gray-300"
                data-testid="delete-password-input"
              />
            </div>
            
            <div>
              <Label htmlFor="delete-confirmation" className="text-gray-700 font-bold">
                Type <span className="text-red-600">DELETE</span> to confirm:
              </Label>
              <Input
                id="delete-confirmation"
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value.toUpperCase())}
                placeholder="Type DELETE"
                className="mt-2 border-2 border-gray-300"
                data-testid="delete-confirmation-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeletePassword('');
                  setDeleteConfirmation('');
                }}
                variant="outline"
                className="flex-1 border-2 border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deletingAccount || deleteConfirmation !== 'DELETE'}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                data-testid="confirm-delete-btn"
              >
                {deletingAccount ? 'Deleting...' : 'Delete My Account'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Admin shortcut button */}
      <AdminShortcut user={user} />
    </div>
  );
};

export default Dashboard;
